import React, { useEffect, useState } from "react";
import { useContext } from "react";
import { AuthContext } from "../AuthContext";
import ManagerNav from './managernav';
import StaffNav from '../staff/staffnav';
import { EnvelopeIcon, UserCircleIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { MapPinIcon } from "@heroicons/react/16/solid";
import { motion } from "framer-motion";
import { getProfilePicSrc } from '../utils/mediaUrl';

const OrganizationalTree = () => {

  const user = useContext(AuthContext);

  const [departments, setDepartments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [company, setCompany] = useState([]);

  const navigate = useNavigate();
  const cardReveal = {
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/getdepartments`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: 'include',
        });
        if (!response.ok) throw new Error("Failed to fetch departments");
        const data = await response.json();
        setDepartments(data.data);
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      }
    };

    const fetchStaff = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/staff', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error("Failed to fetch staff");
        const data = await response.json();
        setStaff(data.data);
      } catch (error) {
        console.error("Failed to fetch staff:", error);
      }
    };

    const fetchCompany = async () => {
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/company`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: 'include',
        });
        if (!response.ok) throw new Error("Failed to fetch company");
        const data = await response.json();
        setCompany(data.data);
      } catch (error) {
        console.error("Failed to fetch company:", error);
      }
    };

    fetchCompany();
    fetchDepartments();
    fetchStaff();
  }, []);

  // Group staff by department
  const staffByDepartment = departments.map((dept) => ({
    ...dept,
    staff: staff.filter((person) => person.Department === dept.Name)
  }));

  // Helper to render staff card
  const renderStaffCard = (person) => {
    const profilePic = getProfilePicSrc(person);

    return (
      
      <motion.div
        key={person.Name}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -4, scale: 1.01 }}
        transition={{ duration: 0.25 }}
        className="rounded-2xl bg-white p-4 border border-slate-200 shadow-[0_12px_26px_rgba(15,23,42,0.08)] hover:shadow-[0_18px_32px_rgba(14,165,233,0.16)] transition-all duration-200"
      >
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-3">
            {profilePic ? (
              <img
                src={profilePic}
                alt={`${person.Name}'s avatar`}
                className="w-16 h-16 rounded-full border-2 border-cyan-200 shadow-md object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center border-2 border-cyan-200 shadow-md">
                <UserCircleIcon className="h-10 w-10 text-white" />
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
              <span className="text-xs text-white font-bold">✓</span>
            </div>
          </div>
          
          <h4 className="font-bold text-slate-900 text-sm mb-1 line-clamp-1">{person.Name}</h4>
          <p className="text-xs text-slate-500 mb-3 font-medium">{person.Role}</p>
          
          <div className="flex gap-2 w-full">
            <button 
              onClick={() => navigate('/chatdashboard')} 
              data-component="organization-tree"
              data-feature="open_team_chat"
              className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-semibold py-2 px-3 rounded-lg transition-all duration-200 active:scale-95 touch-manipulation flex items-center justify-center"
            >
              <EnvelopeIcon className="h-4 w-4 mr-1" />
              Message
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#ecfeff_0%,_#eef2ff_40%,_#f8fafc_100%)]">
      {user.access === "admin" || user.access === "manager" ? (
        <ManagerNav />
      ) : user.access === "staff" ? (
        <StaffNav />
      ) : null}
     
      <div className="px-3 py-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <motion.div
            initial={cardReveal.initial}
            animate={cardReveal.animate}
            transition={{ duration: 0.35 }}
            className="rounded-3xl border border-cyan-100 bg-white/90 p-4 sm:p-6 mb-6 shadow-[0_18px_34px_rgba(14,165,233,0.12)] backdrop-blur-xl"
          >
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-2 flex items-center justify-center">
                <span className="text-2xl sm:text-3xl mr-2">🏢</span>
                Organization Structure
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                Company overview and team structure
              </p>
            </div>
          </motion.div>

          {/* Company Information */}
          {company.map((comp) => (
            <motion.div
              key={comp.CompanyName}
              initial={cardReveal.initial}
              animate={cardReveal.animate}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="rounded-3xl border border-indigo-100 bg-white/90 p-4 sm:p-6 mb-6 shadow-[0_16px_34px_rgba(99,102,241,0.12)] backdrop-blur-xl"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1 mb-4 lg:mb-0">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl sm:text-3xl mr-3">🏢</span>
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
                      {comp.CompanyName}
                    </h2>
                  </div>
                  <p className="text-sm sm:text-base text-slate-600">
                    Your organization at a glance
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 lg:gap-6">
                  <div className="bg-cyan-50 rounded-xl px-4 py-3 border border-cyan-100">
                    <div className="flex items-center">
                      <MapPinIcon className="h-5 w-5 text-cyan-600 mr-2" />
                      <div>
                        <p className="text-xs text-slate-500 font-medium">Location</p>
                        <p className="text-sm font-semibold text-slate-900">{comp.City}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-indigo-50 rounded-xl px-4 py-3 border border-indigo-100">
                    <div className="flex items-center">
                      <span className="text-lg mr-2">👥</span>
                      <div>
                        <p className="text-xs text-slate-500 font-medium">Total Staff</p>
                        <p className="text-sm font-semibold text-slate-900">{staff.length}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-sky-50 rounded-xl px-4 py-3 border border-sky-100">
                    <div className="flex items-center">
                      <span className="text-lg mr-2">🏬</span>
                      <div>
                        <p className="text-xs text-slate-500 font-medium">Departments</p>
                        <p className="text-sm font-semibold text-slate-900">{departments.length}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Departments Grid */}
          <div className="space-y-6">
            {staffByDepartment.length === 0 ? (
              <motion.div initial={cardReveal.initial} animate={cardReveal.animate} className="rounded-2xl bg-white p-8 text-center border border-slate-200 shadow-[0_14px_28px_rgba(15,23,42,0.08)]">
                <span className="text-4xl sm:text-5xl mb-4 block">🏢</span>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">No Departments Found</h3>
                <p className="text-sm sm:text-base text-slate-600">Get started by creating your first department and adding team members.</p>
              </motion.div>
            ) : (
              staffByDepartment.map((dept, idx) => (
                <motion.div
                  key={dept.Name}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, delay: idx * 0.04 }}
                  className="rounded-3xl bg-white/95 shadow-[0_16px_32px_rgba(15,23,42,0.09)] border border-slate-200 p-4 sm:p-6"
                >
                  {/* Department Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <span className="text-xl sm:text-2xl mr-3">🏬</span>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-slate-900">{dept.Name}</h3>
                        <p className="text-xs sm:text-sm text-slate-500">
                          {dept.staff.length} team member{dept.staff.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="bg-cyan-50 rounded-full px-3 py-1 border border-cyan-100">
                      <span className="text-sm font-semibold text-cyan-700">{dept.staff.length}</span>
                    </div>
                  </div>

                  {/* Staff Grid */}
                  {dept.staff.length === 0 ? (
                    <div className="bg-slate-50 rounded-xl p-6 text-center border border-slate-200">
                      <span className="text-3xl sm:text-4xl mb-3 block">👤</span>
                      <p className="text-sm sm:text-base text-slate-700 font-medium">No staff assigned</p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">Add team members to this department</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                      {dept.staff.map(renderStaffCard)}
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationalTree;
